import { useReducer, useRef, type FormEvent } from 'react';
import { LeadPropertyType, type SubmitLeadRequest } from '@pms/shared';
import { useSubmitLead } from '../../hooks/useSubmitLead';
import { extractErrorMessage, errorStatus, extractFieldErrors } from '../../lib/error';
import { FormField, controlClass } from './FormField';
import { HoneypotField } from './HoneypotField';

// --- form model ---------------------------------------------------------------

type FieldKey =
  | 'ownerName'
  | 'email'
  | 'phone'
  | 'propertyName'
  | 'city'
  | 'country'
  | 'propertyType'
  | 'roomsEstimate'
  | 'message';

type Values = Record<FieldKey, string> & { companyWebsite: string };
type Errors = Partial<Record<FieldKey, string>>;

interface State {
  values: Values;
  errors: Errors;
  touched: Partial<Record<FieldKey, boolean>>;
  submitAttempted: boolean;
}

const EMPTY: Values = {
  ownerName: '',
  email: '',
  phone: '',
  propertyName: '',
  city: '',
  country: '',
  propertyType: '',
  roomsEstimate: '',
  message: '',
  companyWebsite: '',
};

const initialState: State = { values: EMPTY, errors: {}, touched: {}, submitAttempted: false };

type Action =
  | { type: 'change'; field: keyof Values; value: string }
  | { type: 'blur'; field: FieldKey }
  | { type: 'setErrors'; errors: Errors }
  | { type: 'submitAttempt' }
  | { type: 'reset' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'change': {
      const values = { ...state.values, [action.field]: action.value };
      // Clear a field's error as soon as it becomes valid again.
      const errors = { ...state.errors };
      if (action.field in errors) {
        const err = validateField(action.field as FieldKey, action.value);
        if (!err) delete errors[action.field as FieldKey];
        else errors[action.field as FieldKey] = err;
      }
      return { ...state, values, errors };
    }
    case 'blur': {
      const err = validateField(action.field, state.values[action.field]);
      const errors = { ...state.errors };
      if (err) errors[action.field] = err;
      else delete errors[action.field];
      return { ...state, touched: { ...state.touched, [action.field]: true }, errors };
    }
    case 'setErrors':
      return { ...state, errors: action.errors };
    case 'submitAttempt':
      return { ...state, submitAttempted: true };
    case 'reset':
      return initialState;
  }
}

// --- validation (mirrors SubmitLeadDto) ---------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(field: FieldKey, raw: string): string {
  const v = raw.trim();
  switch (field) {
    case 'ownerName':
      return !v ? 'Your name is required.' : v.length > 255 ? 'Name is too long.' : '';
    case 'email':
      return !v ? 'Email is required.' : !EMAIL_RE.test(v) ? 'Enter a valid email address.' : '';
    case 'phone':
      return !v ? 'Phone number is required.' : v.length < 3 ? 'Enter a valid phone number.' : '';
    case 'propertyName':
      return !v ? 'Property name is required.' : '';
    case 'city':
      return !v ? 'City / area is required.' : '';
    case 'propertyType':
      return !v ? 'Select a property type.' : '';
    case 'roomsEstimate': {
      if (!v) return 'Approx. number of rooms is required.';
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1) return 'Enter a whole number of 1 or more.';
      if (n > 100000) return 'That seems too large.';
      return '';
    }
    case 'country':
      return v.length > 100 ? 'Country is too long.' : '';
    case 'message':
      return v.length > 2000 ? 'Message is too long (max 2000 characters).' : '';
  }
}

const ORDER: FieldKey[] = [
  'ownerName',
  'email',
  'phone',
  'propertyName',
  'city',
  'country',
  'propertyType',
  'roomsEstimate',
  'message',
];

function validateAll(values: Values): Errors {
  const errors: Errors = {};
  for (const f of ORDER) {
    const err = validateField(f, values[f]);
    if (err) errors[f] = err;
  }
  return errors;
}

const PROPERTY_TYPE_OPTIONS: { value: LeadPropertyType; label: string }[] = [
  { value: LeadPropertyType.HOTEL, label: 'Hotel' },
  { value: LeadPropertyType.RESORT, label: 'Resort' },
  { value: LeadPropertyType.APARTMENT, label: 'Apartment' },
  { value: LeadPropertyType.LODGE, label: 'Lodge' },
  { value: LeadPropertyType.VILLA, label: 'Villa' },
];

// --- component ----------------------------------------------------------------

interface LeadFormProps {
  /** Called on a successful (or enumeration-safe 409) submit → page advances to STATE B. */
  onListed: (email: string, ownerName: string) => void;
}

export function LeadForm({ onListed }: LeadFormProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const mutation = useSubmitLead();
  const formRef = useRef<HTMLFormElement>(null);

  const set = (field: keyof Values) => (value: string) => dispatch({ type: 'change', field, value });
  const blur = (field: FieldKey) => () => dispatch({ type: 'blur', field });
  const describe = (field: FieldKey) => (state.errors[field] ? `${field}-error` : undefined);

  function focusFirstError(errors: Errors) {
    const first = ORDER.find((f) => errors[f]);
    if (first) formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    dispatch({ type: 'submitAttempt' });

    const errors = validateAll(state.values);
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'setErrors', errors });
      focusFirstError(errors);
      return;
    }

    const v = state.values;
    const payload: SubmitLeadRequest = {
      ownerName: v.ownerName.trim(),
      email: v.email.trim(),
      phone: v.phone.trim(),
      propertyName: v.propertyName.trim(),
      city: v.city.trim(),
      propertyType: v.propertyType as LeadPropertyType,
      roomsEstimate: Number(v.roomsEstimate),
      ...(v.country.trim() && { country: v.country.trim() }),
      ...(v.message.trim() && { message: v.message.trim() }),
      companyWebsite: v.companyWebsite, // honeypot — empty for humans
    };

    mutation.mutate(payload, {
      // 201 → advance to STATE B (verify); success no longer lives in the form.
      onSuccess: () => onListed(payload.email, payload.ownerName),
      onError: (err) => {
        if (errorStatus(err) === 400) {
          const fieldErrors = extractFieldErrors(err, ORDER);
          if (Object.keys(fieldErrors).length > 0) {
            dispatch({ type: 'setErrors', errors: fieldErrors });
            focusFirstError(fieldErrors);
          }
          return;
        }
        // Defensive 409 (dedupe reject): treat as soft-success, still advance to
        // STATE B — enumeration-safe, never reveal "email exists" (FE-Q4).
        if (errorStatus(err) === 409) {
          onListed(payload.email, payload.ownerName);
        }
      },
    });
  }

  const status = errorStatus(mutation.error);
  const banner = mutation.isError
    ? status === 429
      ? "You've sent a few requests in a short time. Please wait a minute and try again."
      : status === 400
        ? 'Please fix the highlighted fields and try again.'
        : extractErrorMessage(mutation.error)
    : null;

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={handleSubmit}
      className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8"
      aria-labelledby="lead-form-heading"
    >
      <h2 id="lead-form-heading" className="text-xl font-bold text-brand-navy">
        Tell us about your property
      </h2>
      <p className="mt-1 text-sm text-gray-500">
        It takes about a minute. Fields marked <span className="text-red-600">*</span> are required.
      </p>

      {banner && (
        <div role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {banner}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField id="ownerName" label="Your name" required error={state.errors.ownerName}>
          <input
            id="ownerName" name="ownerName" type="text" autoComplete="name"
            value={state.values.ownerName} onChange={(e) => set('ownerName')(e.target.value)}
            onBlur={blur('ownerName')} aria-invalid={!!state.errors.ownerName}
            aria-describedby={describe('ownerName')} className={controlClass(!!state.errors.ownerName)}
          />
        </FormField>

        <FormField id="email" label="Email" required error={state.errors.email}>
          <input
            id="email" name="email" type="email" autoComplete="email"
            value={state.values.email} onChange={(e) => set('email')(e.target.value)}
            onBlur={blur('email')} aria-invalid={!!state.errors.email}
            aria-describedby={describe('email')} className={controlClass(!!state.errors.email)}
          />
        </FormField>

        <FormField id="phone" label="Phone" required error={state.errors.phone}>
          <input
            id="phone" name="phone" type="tel" autoComplete="tel"
            value={state.values.phone} onChange={(e) => set('phone')(e.target.value)}
            onBlur={blur('phone')} aria-invalid={!!state.errors.phone}
            aria-describedby={describe('phone')} className={controlClass(!!state.errors.phone)}
          />
        </FormField>

        <FormField id="propertyName" label="Property name" required error={state.errors.propertyName}>
          <input
            id="propertyName" name="propertyName" type="text"
            value={state.values.propertyName} onChange={(e) => set('propertyName')(e.target.value)}
            onBlur={blur('propertyName')} aria-invalid={!!state.errors.propertyName}
            aria-describedby={describe('propertyName')} className={controlClass(!!state.errors.propertyName)}
          />
        </FormField>

        <FormField id="city" label="City / area" required error={state.errors.city}>
          <input
            id="city" name="city" type="text"
            value={state.values.city} onChange={(e) => set('city')(e.target.value)}
            onBlur={blur('city')} aria-invalid={!!state.errors.city}
            aria-describedby={describe('city')} className={controlClass(!!state.errors.city)}
          />
        </FormField>

        <FormField id="country" label="Country" error={state.errors.country} hint="Optional">
          <input
            id="country" name="country" type="text" autoComplete="country-name"
            value={state.values.country} onChange={(e) => set('country')(e.target.value)}
            onBlur={blur('country')} aria-invalid={!!state.errors.country}
            aria-describedby={describe('country')} className={controlClass(!!state.errors.country)}
          />
        </FormField>

        <FormField id="propertyType" label="Property type" required error={state.errors.propertyType}>
          <select
            id="propertyType" name="propertyType"
            value={state.values.propertyType} onChange={(e) => set('propertyType')(e.target.value)}
            onBlur={blur('propertyType')} aria-invalid={!!state.errors.propertyType}
            aria-describedby={describe('propertyType')} className={controlClass(!!state.errors.propertyType)}
          >
            <option value="">Select a type…</option>
            {PROPERTY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>

        <FormField id="roomsEstimate" label="Approx. rooms / units" required error={state.errors.roomsEstimate}>
          <input
            id="roomsEstimate" name="roomsEstimate" type="number" min={1} step={1} inputMode="numeric"
            value={state.values.roomsEstimate} onChange={(e) => set('roomsEstimate')(e.target.value)}
            onBlur={blur('roomsEstimate')} aria-invalid={!!state.errors.roomsEstimate}
            aria-describedby={describe('roomsEstimate')} className={controlClass(!!state.errors.roomsEstimate)}
          />
        </FormField>
      </div>

      <div className="mt-5">
        <FormField id="message" label="Anything else?" error={state.errors.message} hint="Optional — tell us about your property">
          <textarea
            id="message" name="message" rows={4} maxLength={2000}
            value={state.values.message} onChange={(e) => set('message')(e.target.value)}
            onBlur={blur('message')} aria-invalid={!!state.errors.message}
            aria-describedby={describe('message')} className={controlClass(!!state.errors.message)}
          />
        </FormField>
      </div>

      <HoneypotField value={state.values.companyWebsite} onChange={set('companyWebsite')} />

      <button
        type="submit"
        disabled={mutation.isPending}
        className="mt-6 flex w-full items-center justify-center rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white transition hover:bg-brand-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {mutation.isPending ? 'Submitting…' : 'Submit your property'}
      </button>
    </form>
  );
}
